#!/usr/bin/env node

/**
 * Runhuman MCP Server - stdio entry point
 *
 * This is the CLI entry point for npx @runhuman/mcp-server.
 * It uses stdio transport for communication with MCP clients like Claude Desktop.
 *
 * @see https://modelcontextprotocol.io/
 */

import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import * as dotenv from 'dotenv';
import { createMcpServer } from './mcp-server-factory.js';
import type { McpServerConfig } from './types.js';

// Load environment variables (optional - for standalone testing)
dotenv.config();

// Configuration
// Priority: CLI args > env vars > defaults
// Usage: node dist/index.js --api-key=<key> [--api-url=https://...]
const args = process.argv.slice(2);
const apiKeyArg = args.find(arg => arg.startsWith('--api-key='))?.split('=')[1];
const apiUrlArg = args.find(arg => arg.startsWith('--api-url='))?.split('=')[1];

const API_URL = apiUrlArg || process.env.RUNHUMAN_API_URL || 'https://runhuman.com';
const API_KEY = apiKeyArg || process.env.RUNHUMAN_API_KEY;

if (!API_KEY) {
  console.error('Error: Authentication token is required');
  console.error('');
  console.error('For Claude Desktop, add to your config:');
  console.error('{');
  console.error('  "mcpServers": {');
  console.error('    "runhuman": {');
  console.error('      "command": "npx",');
  console.error('      "args": ["-y", "@runhuman/mcp-server", "--api-key=<your-token>"]');
  console.error('    }');
  console.error('  }');
  console.error('}');
  console.error('');
  console.error('Recommended: Use a Personal Access Token (PAT) for full access to all tools.');
  console.error('Create one at: https://runhuman.com/dashboard/settings/tokens');
  process.exit(1);
}

/**
 * Validate token format and create configuration.
 * Accepts PATs (rh_pat_*) and API keys (rh_* or legacy qa_live_*) — PATs recommended for full tool access.
 */
function createConfig(apiKey: string, apiUrl: string): McpServerConfig {
  const isPat = apiKey.startsWith('rh_pat_');
  const isApiKey = !isPat && (apiKey.startsWith('rh_') || apiKey.startsWith('qa_live_') || apiKey.startsWith('qa_test_'));

  if (!isPat && !isApiKey) {
    console.error('Error: Invalid token format');
    console.error('');
    console.error('Use a Personal Access Token (rh_pat_*) or API key (rh_*).');
    console.error('PATs are recommended — they work with all MCP tools.');
    console.error('');
    console.error('Create a PAT at: https://runhuman.com/dashboard/settings/tokens');
    process.exit(1);
  }

  if (isApiKey) {
    console.error('Warning: API keys only work with create_job, get_job, and wait.');
    console.error('For full access to all tools, use a Personal Access Token (PAT).');
    console.error('Create one at: https://runhuman.com/dashboard/settings/tokens');
    console.error('');
  }

  return { mode: 'direct', apiUrl, apiKey };
}

// Create config
const config = createConfig(API_KEY, API_URL);

const tokenType = API_KEY.startsWith('rh_pat_') ? 'PAT' : 'API key';
console.error(`Mode: direct (Runhuman API)`);
console.error(`API URL: ${API_URL}`);
console.error(`Using ${tokenType}: ${API_KEY.substring(0, 12)}...`);

// Create the MCP server with appropriate configuration
const server = createMcpServer(config);

/**
 * Start the server with stdio transport
 */
async function main() {
  const transport = new StdioServerTransport();
  await server.connect(transport);
  console.error('Runhuman MCP server running on stdio');
}

main().catch((error) => {
  console.error('Server error:', error);
  process.exit(1);
});
